import { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { httpCheckoutAPI } from 'V2/Services/httpCheckoutAPI';
import { CHECKOUT_SESSION_KEYS } from 'V2/Checkout/constants';
import { VerifyResponse } from 'V2/Checkout/CheckoutVerification/types';

interface UseVerifyZipResult {
  fieldError: string;
  isSubmitting: boolean;
  submit: (zipCode: string) => Promise<void>;
}

export const useVerifyZip = (onLockout: () => void): UseVerifyZipResult => {
  const history = useHistory();
  const [fieldError, setFieldError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(
    async (zipCode: string) => {
      setFieldError('');
      setIsSubmitting(true);
      try {
        const response = await httpCheckoutAPI.post({
          url: '/checkout/verify',
          data: { zipCode: zipCode },
        });
        const data: VerifyResponse = response.data;

        if (data.verified) {
          if (data.insuranceApplicationId) {
            sessionStorage.setItem(
              CHECKOUT_SESSION_KEYS.appId,
              data.insuranceApplicationId
            );
          }
          if (data.quoteId) {
            sessionStorage.setItem(CHECKOUT_SESSION_KEYS.quoteId, data.quoteId);
          }
          if (data.paymentPlanId) {
            sessionStorage.setItem(
              CHECKOUT_SESSION_KEYS.paymentPlanId,
              data.paymentPlanId
            );
          }
          history.push('/checkout/pay');
        } else {
          const remaining = data.remainingAttempts ?? 0;
          setFieldError(
            `Incorrect ZIP code. ${remaining} attempt${
              remaining !== 1 ? 's' : ''
            } remaining.`
          );
        }
      } catch (err: any) {
        if (err.response?.status === 429) {
          onLockout();
        } else {
          setFieldError('Something went wrong. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [history, onLockout]
  );

  return { fieldError, isSubmitting, submit };
};
